
import React, { useState, useEffect, useRef } from 'react';
import { debugLog, debugError } from '@/config/debug';

interface TranscriptionTimerProps {
  isRecording: boolean;
  onTimeUpdate?: (formattedTime: string, totalSeconds: number) => void;
  shouldReset?: boolean;
  isFinished?: boolean;
}

export const TranscriptionTimer = ({ isRecording, onTimeUpdate, shouldReset, isFinished }: TranscriptionTimerProps) => {
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [hasEverRecorded, setHasEverRecorded] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRecordingState = useRef(false);
  const accumulatedTime = useRef(0);
  const sessionStartTime = useRef<number | null>(null);

  // Format time helper
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const formatted = `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    
    debugLog('TIMER', '⏰ TIMER FORMAT:', {
      inputSeconds: seconds,
      calculatedMinutes: minutes,
      calculatedSeconds: secs,
      formattedResult: formatted
    });
    
    return formatted;
  };

  // Reset timer when shouldReset changes to true
  useEffect(() => {
    if (shouldReset) {
      debugLog('TIMER', '🔄 TIMER: Resetting timer completely');
      setTotalSeconds(0);
      setHasEverRecorded(false);
      accumulatedTime.current = 0;
      sessionStartTime.current = null;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      debugLog('TIMER', '🔄 TIMER: Timer reset, calling onTimeUpdate with 0');
      onTimeUpdate?.(formatTime(0), 0);
    }
  }, [shouldReset, onTimeUpdate]);

  // Hide timer when consultation is finished
  useEffect(() => {
    if (isFinished) {
      debugLog('TIMER', '🏁 TIMER: Consultation finished, hiding timer');
      setHasEverRecorded(false);
      setTotalSeconds(0);
      accumulatedTime.current = 0;
      sessionStartTime.current = null;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isFinished]);

  useEffect(() => {
    debugLog('TIMER', '⏰ TIMER: Recording state change:', {
      isRecording,
      lastRecordingState: lastRecordingState.current,
      accumulatedTime: accumulatedTime.current,
      sessionStartTime: sessionStartTime.current
    });

    if (isRecording && !lastRecordingState.current) {
      // Started recording - start new session from accumulated time
      debugLog('TIMER', '🎬 TIMER: Starting recording session, accumulated time:', accumulatedTime.current);
      setHasEverRecorded(true);
      sessionStartTime.current = Date.now();
      
      intervalRef.current = setInterval(() => {
        if (sessionStartTime.current) {
          const sessionTime = Math.floor((Date.now() - sessionStartTime.current) / 1000);
          const newTotalSeconds = accumulatedTime.current + sessionTime;
          setTotalSeconds(newTotalSeconds);
          
          const formattedTime = formatTime(newTotalSeconds);
          debugLog('TIMER', '⏰ TIMER: TICK - Updating time:', {
            sessionTime,
            accumulatedTime: accumulatedTime.current,
            newTotalSeconds,
            formattedTime,
            willCallOnTimeUpdate: !!onTimeUpdate
          });
          
          // CRÍTICO: Verificar que estamos enviando valores válidos
          if (newTotalSeconds <= 0) {
            debugError('TIMER', '🚨 TIMER: SENDING INVALID TIME TO PARENT!', {
              newTotalSeconds,
              formattedTime,
              sessionTime,
              accumulatedTime: accumulatedTime.current
            });
          }
          
          onTimeUpdate?.(formattedTime, newTotalSeconds);
        }
      }, 1000);
    } else if (!isRecording && lastRecordingState.current) {
      // Stopped recording - accumulate time and pause (DON'T RESET)
      debugLog('TIMER', '⏸️ TIMER: Pausing recording session');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      if (sessionStartTime.current) {
        const sessionTime = Math.floor((Date.now() - sessionStartTime.current) / 1000);
        accumulatedTime.current += sessionTime;
        sessionStartTime.current = null;
        debugLog('TIMER', '📊 TIMER: Final accumulated time after pause:', accumulatedTime.current, 'seconds');
        
        // Mantener el tiempo total mostrado y actualizar el callback
        const finalTotalSeconds = accumulatedTime.current;
        setTotalSeconds(finalTotalSeconds);
        const formattedTime = formatTime(finalTotalSeconds);
        debugLog('TIMER', '⏸️ TIMER: Final pause update -', {
          finalTotalSeconds,
          formattedTime,
          willCallOnTimeUpdate: !!onTimeUpdate
        });
        
        // CRÍTICO: Verificar que estamos enviando valores válidos al pausar
        if (finalTotalSeconds <= 0) {
          debugError('TIMER', '🚨 TIMER: SENDING INVALID TIME TO PARENT ON PAUSE!', {
            finalTotalSeconds,
            formattedTime,
            accumulatedTime: accumulatedTime.current
          });
        }
        
        onTimeUpdate?.(formattedTime, finalTotalSeconds);
      }
    }

    lastRecordingState.current = isRecording;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRecording, onTimeUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debugLog('TIMER', '🧹 TIMER: Timer cleanup');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Solo mostrar el timer si está grabando o si ha grabado antes (en pausa) y no ha finalizado
  if ((!isRecording && !hasEverRecorded) || isFinished) {
    debugLog('TIMER', '👁️ TIMER: Timer hidden -', {
      isRecording,
      hasEverRecorded,
      isFinished
    });
    return null;
  }

  debugLog('TIMER', '👁️ TIMER: Timer visible with totalSeconds:', totalSeconds, 'formatted:', formatTime(totalSeconds));

  return (
    <span className="text-xs text-gray-500 ml-2">
      {formatTime(totalSeconds)}
    </span>
  );
};
